import { useContext } from "react";
import { NavigateOptions, useNavigate } from "react-router-dom";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";

import { isKebabCase, isPascalCase, isValidEmail, isValidNonce } from "../utils";
import { AuthenticationContext, AuthenticationDispatchContext } from "./AuthenticationContext";
import { AuthenticationConfigurationContext } from "./Configuration";

import * as api from "../../../api/designer/authentication";
import * as userProfileApi from "../../../api/designer/userProfile";
import { makeUrl } from "../../../api/makeUrl";

import { type UserPasskey } from "../types";
import { AuthenticationViews, type AuthenticationAction, type State } from "./types";
import { appStrings } from "./strings";

export const reducer = (state: State, action: AuthenticationAction): State => {
    switch (action.type) {
        case "SET_ACTIVE_VIEW":
            return { ...state, activeView: action.payload };
        case "SET_CLIENT_ERRORS":
            return { ...state, clientErrors: action.payload };
        case "SET_IS_LOADING":
            return { ...state, isLoading: action.payload };
        case "SET_SERVER_ERROR":
            return { ...state, serverError: action.payload };
        case "SET_EMAIL":
            return { ...state, email: action.payload };
        case "SET_FIRST_NAME":
            return { ...state, firstName: action.payload };
        case "SET_LAST_NAME":
            return { ...state, lastName: action.payload };
        case "SET_LAST_SESSION_COOKIE":
            return { ...state, lastSessionCookie: action.payload };
        case "SET_REMEMBER_ME":
            return { ...state, rememberMe: action.payload };
        case "SET_VERIFICATION_CODE":
            return { ...state, verificationCode: action.payload };
        case "SET_VERIFIED_NONCE_TOKEN":
            return { ...state, verifiedNonceToken: action.payload };
        case "SET_LOGIN_CHECK_TIMER":
            return { ...state, loginCheckTimer: action.payload };
        case "SET_REDIRECT_URL":
            return { ...state, redirectUrl: action.payload };
        case "SET_SIGN_IN_ID":
            return { ...state, signInId: action.payload };
        case "SET_USER_NAME":
            return { ...state, userName: action.payload };
        case "SET_TOTP_KEY":
            return { ...state, totpKey: action.payload };
        case "SET_TOTP_QR_CODE_URI":
            return { ...state, totpQrCodeUri: action.payload };
        case "SET_RECOVERY_CODES":
            return { ...state, recoveryCodes: action.payload };
        case "SET_IS_TOTP_ENABLED":
            return { ...state, isTotpEnabled: action.payload };
        case "SET_PASSKEYS":
            return { ...state, passkeys: action.payload };
        default:
            return state;
    }
};

export const defaultState: State = {
    activeView: undefined,
    clientErrors: [],
    email: "",
    firstName: "",
    isLoading: false,
    lastName: "",
    lastSessionCookie: "",
    redirectUrl: "",
    rememberMe: false,
    serverError: "",
    signInId: "",
    userName: "",
    verificationCode: "",
    verifiedNonceToken: "",
    passkeys: [],
};

const useAuthenticationStore = () => {
    const state = useContext(AuthenticationContext);
    const dispatch = useContext(AuthenticationDispatchContext);
    const configuration = useContext(AuthenticationConfigurationContext);
    const navigate = useNavigate();

    /**
     * Asynchronously retrieves a user-friendly error message from an Error object.
     * Currently has a number of checks because the error message signatures are inconsistent.
     *
     * @param {unknown} error - The error object from which to extract the message.
     * @returns {Promise<string>} A promise that resolves to a string containing the error message.
     */
    async function getErrorMessage(error: unknown): Promise<string> {
        let msgKey = "";
        if (typeof error === "string") {
            msgKey = error;
        } else if (error instanceof Error) {
            // const error = err as Error;
            msgKey = error.message;
            if (error.message === "AccountLockedOutCannotReset") {
                msgKey = "error-account-locked-out-no-reset";
            } else if (error.message.indexOf("LockedOut") >= 0) {
                msgKey = "error-account-locked-out";
            } else if (error.message === "InvalidUserNamePassword") {
                msgKey = "error-invalid-password";
            }
            if (error.cause) {
                const cause = error.cause as { message?: string };
                if (
                    cause.message &&
                    cause.message !== msgKey &&
                    !isKebabCase(cause.message) &&
                    !isPascalCase(cause.message) &&
                    !cause.message.includes("unexpected error")
                ) {
                    return cause.message;
                }
            }
        }

        try {
            return appStrings[msgKey] ?? msgKey;
        } catch {
            return msgKey;
        }
    }

    function clearErrors() {
        dispatch({
            type: "SET_CLIENT_ERRORS",
            payload: [],
        });
        dispatch({
            type: "SET_SERVER_ERROR",
            payload: "",
        });
    }

    async function getClientVerificationToken(form: HTMLFormElement): Promise<string> {
        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            if (window.CareEv?.WaitForClientVerificationToken) {
                await window.CareEv.WaitForClientVerificationToken();
            }
            const formData = new FormData(form);
            return formData.get("ClientVerificationToken") as string;
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    }

    async function startJoin() {
        if (!isValidEmail(state.email)) {
            return;
        }
        clearErrors();
        dispatch({ type: "SET_IS_LOADING", payload: true });

        try {
            await api.startJoin(state.email, state.firstName, state.lastName);
            dispatch({ type: "SET_IS_LOADING", payload: false });
            setActiveView(AuthenticationViews.joinSuccess);
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    }

    async function finishJoin() {
        clearErrors();
        dispatch({ type: "SET_IS_LOADING", payload: false });
        // setLoginCookie(state.email, state.rememberMe);
        window.location.href = makeUrl(state.redirectUrl ?? "~/");
        return;
    }

    async function finishLogin(password: string, clientVerificationToken: string) {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            await api.finishLogin(state.email, password, state.signInId, clientVerificationToken);
            window.location.href = makeUrl(state.redirectUrl);
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        }
        dispatch({ type: "SET_IS_LOADING", payload: false });
    }

    async function forgotPassword() {
        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            await api.forgotPassword(state.signInId, state.email);
            dispatch({ type: "SET_IS_LOADING", payload: false });
            dispatch({ type: "SET_USER_NAME", payload: "" });
            setActiveView(AuthenticationViews.verifyPasswordResetNonce);
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        }
        dispatch({ type: "SET_IS_LOADING", payload: false });
    }

    async function verifySetPasswordNonce(email: string, nonce: string, isInit: boolean) {
        if (!isValidNonce(nonce)) {
            return;
        }
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            const userName = await api.verifyNonce(email, nonce);
            dispatch({ type: "SET_USER_NAME", payload: userName });
            dispatch({ type: "SET_VERIFIED_NONCE_TOKEN", payload: nonce });
            setActiveView(isInit ? AuthenticationViews.initializePassword : AuthenticationViews.resetPassword);
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        }
        dispatch({ type: "SET_IS_LOADING", payload: false });
    }

    async function validatePasswordRequirements(password: string) {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        let response = "";
        try {
            const result = await api.validatePasswordRequirements(state.email, password);
            response = result.Error as string;
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
            response = errorMessage;
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
        return response;
    }

    async function initializePassword(password: string) {
        if (!isValidNonce(state.verifiedNonceToken)) {
            return;
        }
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            await api.initializePassword(state.userName, password, state.verifiedNonceToken);
            await finishJoin();
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        }
        dispatch({ type: "SET_IS_LOADING", payload: false });
    }

    async function snooze2FA() {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            await api.snooze2fa();
            await finishJoin();
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        }
        dispatch({ type: "SET_IS_LOADING", payload: false });
    }

    async function enableTotp(key: string, confirmationCode: string) {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            if (isLogin()) {
                const response = await api.enableTotp2fa(key, confirmationCode);
                if (response.recoveryCodes && response.recoveryCodes.length) {
                    dispatch({
                        type: "SET_RECOVERY_CODES",
                        payload: response.recoveryCodes,
                    });
                    setActiveView(AuthenticationViews.recoveryCodes);
                } else {
                    await finishJoin();
                }
            } else {
                if (await userProfileApi.enableTotp(key, confirmationCode)) {
                    setActiveView(AuthenticationViews.mfa);
                    dispatch({
                        type: "SET_IS_TOTP_ENABLED",
                        payload: true,
                    });
                } else {
                    dispatch({
                        type: "SET_SERVER_ERROR",
                        payload: await getErrorMessage("totp-enable-failed"),
                    });
                }
            }
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        }
        dispatch({ type: "SET_IS_LOADING", payload: false });
    }

    async function disableTotp() {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            await userProfileApi.disableTotp();
            dispatch({
                type: "SET_IS_TOTP_ENABLED",
                payload: false,
            });
            dispatch({
                type: "SET_TOTP_KEY",
                payload: null,
            });
            dispatch({
                type: "SET_TOTP_QR_CODE_URI",
                payload: null,
            });
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        }
        dispatch({ type: "SET_IS_LOADING", payload: false });
    }

    async function verifyMfa(
        code: string,
        clientVerificationToken: string,
        rememberMe?: boolean,
        isRecoveryCode?: boolean,
    ) {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            let response;
            if (isRecoveryCode) {
                response = await api.verifyRecoveryCode(code, clientVerificationToken, rememberMe);
            } else {
                response = await api.verifyTotp2fa(code, clientVerificationToken, rememberMe);
            }
            if (response.error) {
                throw new Error(response.error.Code);
            } else if (response.redirectPath) {
                const redirect = new URL(response.redirectPath, document.baseURI);
                const errorCode = redirect.searchParams.get("errorCode");
                if (errorCode) {
                    throw new Error(errorCode);
                }
                location.href = response.redirectPath;
            }

            await finishJoin();
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        }
        dispatch({ type: "SET_IS_LOADING", payload: false });
    }

    async function addPasskey(refreshOnly = false) {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            const options = await api.getPasskeyRegistrationOptions(getPasskeyBaseUrl());
            const startResp = await startRegistration(options);
            await api.addPasskey(getPasskeyBaseUrl(), startResp);
            if (refreshOnly) {
                await fetchPasskeys();
            } else {
                await generateRecoveryCodes();
            }
        } catch {
            const errorMessage = await getErrorMessage(new Error("error-add-passkey-failed"));
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        }
        dispatch({ type: "SET_IS_LOADING", payload: false });
    }

    async function verifyPasskey() {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            const options = await api.getPasskeyAuthenticationOptions(getPasskeyBaseUrl());

            const startResp = await startAuthentication(options);

            await api.verifyPasskey(getPasskeyBaseUrl(), startResp);
            await finishJoin();
            return true;
        } catch {
            const errorMessage = await getErrorMessage("error-verify-passkey-failed");
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
        return false;
    }

    async function hasPasskey() {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            const resp = await api.getPasskeyAuthenticationOptions(getPasskeyBaseUrl());
            return resp;
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    }

    async function fetchPasskeys() {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            const list = await api.getPasskeys(getPasskeyBaseUrl());
            dispatch({
                type: "SET_PASSKEYS",
                payload: list as UserPasskey[],
            });
            return list;
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    }

    async function renamePasskey(passkey: UserPasskey) {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            await api.savePasskey(getPasskeyBaseUrl(), passkey);
            await fetchPasskeys();
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    }

    async function deletePasskey(id: string) {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            await api.deletePasskey(getPasskeyBaseUrl(), id);
            await fetchPasskeys();
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    }

    async function generateTotpKey() {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            const response = await userProfileApi.generateTotpKey();
            dispatch({
                type: "SET_TOTP_KEY",
                payload: response.Key,
            });
            dispatch({
                type: "SET_TOTP_QR_CODE_URI",
                payload: response.QrCodeUri,
            });
            return !!(response.Key && response.QrCodeUri);
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    }

    async function generateRecoveryCodes() {
        clearErrors();

        dispatch({ type: "SET_IS_LOADING", payload: true });
        try {
            const response = state.signInId ? await api.generateRecoveryCodes() : await userProfileApi.generateRecoveryCodes();
            if (response.recoveryCodes) {
                dispatch({
                    type: "SET_RECOVERY_CODES",
                    payload: response.recoveryCodes,
                });
                setActiveView(AuthenticationViews.recoveryCodes);
            }
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    }

    async function finishPasswordReset(password: string) {
        if (!isValidPassword(password)) {
            return;
        }
        clearErrors();
        dispatch({
            type: "SET_IS_LOADING",
            payload: true,
        });
        try {
            await api.resetPassword(state.userName, password, state.verifiedNonceToken);
            window.location.href = makeUrl(state.redirectUrl);
        } catch (error) {
            const errorMessage = await getErrorMessage(error);
            dispatch({
                type: "SET_SERVER_ERROR",
                payload: errorMessage,
            });
        }
        dispatch({
            type: "SET_IS_LOADING",
            payload: false,
        });
    }

    function isValidPassword(password: string) {
        if (!password) {
            return false;
        }
        return password.length >= 8 && password.length <= 64;
    }

    function setRememberMe(rememberMe: boolean) {
        dispatch({
            type: "SET_REMEMBER_ME",
            payload: rememberMe,
        });
    }

    function checkLogin() {
        const sessionCookie = configuration.cookies.get(configuration.sessionCookieName);
        if (!state.lastSessionCookie && sessionCookie) {
            clearInterval(state.loginCheckTimer);
            window.location.href = makeUrl(state.redirectUrl);
        }
    }

    function setActiveView(view: AuthenticationViews, options?: NavigateOptions) {
        navigate("../" + view, options);
    }

    function isLogin() {
        return !!state.signInId;
    }

    function getPasskeyBaseUrl(): string {
        if (!configuration.passkeyServerBaseUrl) {
            return ""
        }
        if (configuration.passkeyServerBaseUrl.endsWith("/")) {
            return configuration.passkeyServerBaseUrl.substring(0, configuration.passkeyServerBaseUrl.length - 1);
        }
        return configuration.passkeyServerBaseUrl
    }

    return {
        getClientVerificationToken,
        isLogin,
        checkLogin,
        finishJoin,
        finishLogin,
        finishPasswordReset,
        forgotPassword,
        getErrorMessage,
        setActiveView,
        setRememberMe,
        startJoin,
        verifySetPasswordNonce,
        validatePasswordRequirements,
        initializePassword,
        snooze2FA,
        enableTotp,
        verifyMfa,
        addPasskey,
        hasPasskey,
        verifyPasskey,
        fetchPasskeys,
        renamePasskey,
        deletePasskey,
        disableTotp,
        generateTotpKey,
        generateRecoveryCodes,
        state,
        dispatch,
    };
};

export default useAuthenticationStore;
